import { useCallback, useEffect, useState } from 'react';
import { access, type Role, type User } from '../api/access';
import { chart, type SaveUserGroup, type UserGroup } from '../api/chart';
import {
  IconCircleAlert,
  IconPencil,
  IconPlus,
  IconRefreshCw,
  IconTrash2,
  IconUsers,
} from '../icons';
import { CODE_MAX, fold, matches } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, SearchInput, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * Named sets of people, cutting across the chart.
 *
 * A seat says where somebody sits and a trade says what they are. A group says who belongs
 * together for a purpose that fits neither: the people a report is filtered by, and the
 * people something is granted to as a set.
 *
 * Roles only, no reach. Reach belongs to the seat so that "why does this person see this
 * store" keeps one answer — the screen says so rather than leaving it to be discovered.
 */

function suggestCode(name: string): string {
  return fold(name)
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

const blank: SaveUserGroup = {
  code: '',
  name: '',
  description: null,
  isActive: true,
  roleIds: [],
  memberIds: [],
};

export function UserGroups() {
  const t = useTranslate();
  const [rows, setRows] = useState<UserGroup[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [people, setPeople] = useState<User[]>([]);
  // Finding one person among sixty by scrolling was the slow part of every group edit
  // (field test, 2 November).
  const [memberTerm, setMemberTerm] = useState('');
  const [draft, setDraft] = useState<{ id: string | null; values: SaveUserGroup } | null>(null);
  const [doomed, setDoomed] = useState<UserGroup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [codeFollows, setCodeFollows] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, allRoles, allUsers] = await Promise.all([
        chart.groups.list(),
        access.roles.list(),
        access.users.list(),
      ]);
      setRows(list);
      setRoles(allRoles.filter((r) => r.isActive));
      setPeople(allUsers.filter((u) => u.isActive));
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const values = draft?.values;
  const set = (change: Partial<SaveUserGroup>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  /** Works from the previous draft: two boxes ticked in one render must not lose the first. */
  const toggle = (field: 'roleIds' | 'memberIds') => (id: string) =>
    setDraft((previous) => {
      if (!previous) return previous;

      const held = previous.values[field].includes(id);

      return {
        ...previous,
        values: {
          ...previous.values,
          [field]: held
            ? previous.values[field].filter((x) => x !== id)
            : [...previous.values[field], id],
        },
      };
    });

  const toggleRole = toggle('roleIds');
  const toggleMember = toggle('memberIds');

  async function save() {
    if (!draft || busy) return;

    setBusy(true);
    try {
      const body: SaveUserGroup = {
        ...draft.values,
        code: draft.values.code.trim(),
        name: draft.values.name.trim(),
        description: draft.values.description?.trim() || null,
      };

      if (draft.id) await chart.groups.update(draft.id, body);
      else await chart.groups.create(body);

      setDraft(null);
      setPanelError(null);
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: UserGroup) {
    setBusy(true);
    try {
      await chart.groups.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const canSave = Boolean(values && values.name.trim() && (draft?.id || values.code.trim()));

  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  const columns: Column<UserGroup>[] = [
    {
      id: 'name',
      header: t('common.name'),
      className: 'is-primary',
      cell: (row) => (
        <>
          {row.name}
          {row.description ? <div className="nv-hint">{row.description}</div> : null}
        </>
      ),
      skeletonWidth: '44%',
    },
    {
      id: 'code',
      header: t('common.code'),
      width: '1%',
      cell: (row) => <span className="nv-code">{row.code}</span>,
      skeletonWidth: '72px',
    },
    {
      id: 'members',
      header: t('groups.members'),
      cell: (row) =>
        row.members.length === 0 ? (
          <span className="nv-subtle">{t('common.none')}</span>
        ) : (
          <span className="nv-badge-group">
            {row.members.slice(0, 4).map((m) => (
              <Badge key={m.id} tone="neutral" shape="square">{m.fullName}</Badge>
            ))}
            {row.members.length > 4 ? (
              <Badge tone="neutral">{t('groups.andMore', { count: row.members.length - 4 })}</Badge>
            ) : null}
          </span>
        ),
      skeletonWidth: '66%',
    },
    {
      id: 'roles',
      header: t('groups.grants'),
      cell: (row) =>
        row.roleIds.length === 0 ? (
          <span className="nv-subtle">{t('groups.noGrants')}</span>
        ) : (
          <span className="nv-badge-group">
            {row.roleIds.map((id) => (
              <Badge key={id} tone="scale" shape="square">{roleName(id)}</Badge>
            ))}
          </span>
        ),
      skeletonWidth: '50%',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <Badge tone={row.isActive ? 'comment' : 'neutral'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      skeletonWidth: '62px',
    },
  ];

  function open(row: UserGroup) {
    setPanelError(null);
    setCodeFollows(false);
    setDraft({
      id: row.id,
      values: {
        code: row.code,
        name: row.name,
        description: row.description,
        isActive: row.isActive,
        roleIds: row.roleIds,
        memberIds: row.members.map((m) => m.userId),
      },
    });
  }

  function openNew() {
    setPanelError(null);
    setCodeFollows(true);
    setDraft({ id: null, values: { ...blank } });
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.people'), t('nav.groups')]}
        title={t('groups.title')}
        subtitle={t('groups.subtitle')}
        action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('groups.add')}</Button>}
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('groups.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={open}
          rowActions={(row) => {
            const blocked =
              row.roleIds.length > 0 ? t('groups.grantsCannotDelete', { count: row.roleIds.length }) : null;

            return (
              <>
                <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => open(row)} />
                <RowMenu
                  label={t('common.moreActions')}
                  items={[
                    {
                      label: blocked ?? t('common.delete'),
                      icon: IconTrash2,
                      danger: true,
                      disabled: busy || blocked !== null,
                      onSelect: () => setDoomed(row),
                    },
                  ]}
                />
              </>
            );
          }}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconUsers}
              title={t('groups.empty')}
              text={t('groups.emptyText')}
              action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('groups.add')}</Button>}
            />
          }
        />
      </Card>

      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'groups.editTitle' : 'groups.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!canSave || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values ? (
          <>
            <Field label={t('common.name')} required>
              {({ id }) => (
                <TextInput
                  id={id}
                  placeholder="İç Denetim Ekibi"
                  value={values.name}
                  onChange={(e) =>
                    set({
                      name: e.target.value,
                      ...(codeFollows ? { code: suggestCode(e.target.value) } : {}),
                    })
                  }
                />
              )}
            </Field>

            <Field label={t('common.code')} hint={t('groups.codeHint')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  maxLength={CODE_MAX}
                  value={values.code}
                  onChange={(e) => {
                    setCodeFollows(false);
                    set({ code: e.target.value });
                  }}
                />
              )}
            </Field>

            <Field label={t('common.description')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('common.optional')}
                  value={values.description ?? ''}
                  onChange={(e) => set({ description: e.target.value || null })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('groups.members')}</h3>
              <p className="nv-hint">{t('groups.membersHint')}</p>

              <SearchInput value={memberTerm} onValueChange={setMemberTerm} placeholder={t('common.search')} />

              <div className="nv-permission-list">
                {people.filter((person) => matches(`${person.fullName} ${person.email}`, memberTerm)).map((person) => (
                  <label className="nv-permission" key={person.id}>
                    <input
                      type="checkbox"
                      checked={values.memberIds.includes(person.id)}
                      onChange={() => toggleMember(person.id)}
                    />
                    <span>
                      {person.fullName}
                      <span className="nv-hint">{person.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('groups.grants')}</h3>
              <p className="nv-hint">{t('groups.grantsHint')}</p>

              <div className="nv-permission-list">
                {roles.map((role) => (
                  <label className="nv-permission" key={role.id}>
                    <input
                      type="checkbox"
                      checked={values.roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span>
                      {role.name}
                      {role.description ? <span className="nv-hint">{role.description}</span> : null}
                    </span>
                  </label>
                ))}
              </div>

              {values.roleIds.length > 0 ? (
                <Alert tone="info">
                  {t('groups.grantsWarning', { count: values.memberIds.length })}
                </Alert>
              ) : null}
            </div>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('common.active')}
              />
              <p className="nv-hint">{t('groups.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        <p>{t('common.deleteWarning')}</p>
      </ConfirmDialog>
    </>
  );
}
